<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { Bookmark, BookmarkPlus, Check, Loader2, Plus, Users } from 'lucide-svelte';

  import ConfirmDialog from '$lib/components/ConfirmDialog.svelte';
  import SavedListDialog from '$lib/components/SavedListDialog.svelte';
  import { notifications } from '$lib/stores/notifications';
  import { loadSavedLists, makeSavedList, savedLists } from '$lib/stores/savedlists';
  import {
    addRecordsToList,
    PartialAdd,
    type ListScope,
    type SavedListRecordInput
  } from '$lib/api/savedlists';

  /**
   * Putting things into a saved list.
   *
   * One component for all three places that do it — a result row, the whole
   * selection, and the company profile — because the interesting part is the
   * same in each: which list, what to say when it worked, and what to say when
   * it half worked.
   *
   * # It reports added against requested
   *
   * The API returns both, and they differ for two ordinary reasons: the record
   * was already there, or it no longer exists. A control that showed one number
   * could not tell the customer which happened, and "40 added" for a request of
   * forty-six reads as a bug rather than as six duplicates.
   *
   * # Adding twice is not an error
   *
   * Worth knowing when reading the copy below. A customer pressing this cannot
   * know what is in the list already, so the quiet case — "nothing new" — is a
   * normal outcome and is worded as one.
   */

  interface Props {
    /**
     * What to add. Read when the menu opens rather than held, so a caller can
     * pass the current selection without this component subscribing to it.
     */
    records: () => SavedListRecordInput[];

    /** How many `records()` will return, for the label and the confirmation. */
    count: number;

    /** Show the label beside the icon. Off for the tight controls on a row. */
    withLabel?: boolean;

    /** The label, when shown. Defaults to a plain "add to list". */
    label?: string;

    /** Ask first above this many. Bulk adds are not obviously reversible. */
    confirmAbove?: number;

    /**
     * Whether this sits in the top bar or on a row.
     *
     * The bar's controls are a row of bordered buttons of one height, and a
     * borderless one among them reads as text that happens to be clickable.
     * On a result row the opposite holds: a bordered button beside a company
     * name is a second thing competing with it, so there the control is quiet
     * until hovered.
     */
    variant?: 'row' | 'bar';
  }

  let {
    records,
    count,
    withLabel = false,
    label = '',
    confirmAbove = 100,
    variant = 'row'
  }: Props = $props();

  let open = $state(false);
  let busy = $state('');
  let making = $state(false);
  let container = $state<HTMLElement | null>(null);
  let trigger = $state<HTMLButtonElement | null>(null);

  /** The list just added to, so the control can say so without a banner. */
  let justAdded = $state(false);

  /**
   * Where the menu is drawn, in the window rather than in the page.
   *
   * # Why it is not simply positioned against the trigger
   *
   * It was, and the employees table clipped it. That table scrolls sideways on
   * a narrow window, and `overflow-x: auto` cannot clip one axis alone —
   * `overflow-y` computes to `auto` with it — so a menu opening below the last
   * row extended past the container and had to be scrolled to. The same is
   * true of every scrolling area this control sits in, which is most of them.
   *
   * A fixed menu belongs to the viewport and no ancestor can crop it. It stays
   * a DOM child of the trigger's container, so the click-outside test and the
   * focus order are unchanged; only the painting moves.
   *
   * # And it flips rather than overflowing
   *
   * Anchored below the trigger normally, above it when the room below is too
   * small — which is the last row of any table long enough to reach the foot
   * of the window. Anchoring the flipped case by its *bottom* edge means the
   * height does not have to be known before it is rendered.
   */
  let placement = $state('');

  /** The menu's width, matching `w-64`. Needed to right-align by hand. */
  const menuWidth = 256;

  /** How much room the menu wants below the trigger before it flips up. */
  const wantedBelow = 240;

  function place() {
    const rect = trigger?.getBoundingClientRect();
    if (!rect) return;

    // Right edges aligned, as `right-0` did, but kept inside the window: on a
    // narrow screen a control near the left would otherwise be placed off it.
    const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));

    const below = window.innerHeight - rect.bottom;
    const vertical =
      below < wantedBelow && rect.top > below
        ? `bottom: ${Math.round(window.innerHeight - rect.top + 4)}px; max-height: ${Math.round(rect.top - 12)}px;`
        : `top: ${Math.round(rect.bottom + 4)}px; max-height: ${Math.round(below - 12)}px;`;

    placement = `left: ${Math.round(left)}px; ${vertical}`;
  }

  function toggle() {
    if (open) {
      open = false;
      return;
    }
    place();
    open = true;
  }

  function onWindowClick(event: MouseEvent) {
    if (!open || !container) return;
    if (!container.contains(event.target as Node)) open = false;
  }

  /**
   * Follow the trigger while anything scrolls.
   *
   * Capturing, because the scroll that moves the trigger is usually a
   * container's rather than the window's, and those do not bubble. Re-placing
   * rather than closing: a menu that vanishes when the table underneath it
   * moves a pixel is a menu that cannot be used with a trackpad.
   */
  function onScrollOrResize() {
    if (open) place();
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && open) {
      event.stopPropagation();
      close();
    }
  }

  /** The list waiting on a confirmation, when the set is a large one. */
  let confirming = $state<{ id: string; name: string; count: number } | null>(null);

  /**
   * Add, asking first when the set is large.
   *
   * A bulk add can be thirty requests and is not obviously undone — the way
   * back is removing the records one screen at a time. `ConfirmDialog` rather
   * than the browser's own `confirm`, which blocks the thread and looks like
   * nothing else in the app.
   */
  function requestAdd(listID: string, listName: string) {
    const wanted = records();
    if (wanted.length === 0) return;

    if (wanted.length > confirmAbove) {
      confirming = { id: listID, name: listName, count: wanted.length };
      open = false;
      return;
    }
    void addTo(listID, listName);
  }

  async function addTo(listID: string, listName: string) {
    const wanted = records();
    if (wanted.length === 0) return;

    busy = listID;
    try {
      const outcome = await addRecordsToList(listID, wanted);
      await loadSavedLists();

      if (outcome.added === 0) {
        notifications.info($_('savedLists.add.nothingNew', { values: { name: listName } }));
      } else if (outcome.added < outcome.requested) {
        // Both numbers, because the gap is the interesting part and the
        // customer is the only one who can judge whether it matters.
        notifications.success(
          $_('savedLists.add.partly', {
            values: { added: outcome.added, requested: outcome.requested, name: listName }
          })
        );
      } else {
        notifications.success(
          $_('savedLists.add.done', { values: { count: outcome.added, name: listName } })
        );
      }

      justAdded = true;
      setTimeout(() => (justAdded = false), 2000);
      open = false;
    } catch (error) {
      if (error instanceof PartialAdd) {
        // A run that stopped part-way. Saying only "failed" would be wrong
        // about the records that did get in, and the customer's next move —
        // trying again — is safe precisely because adding twice is not an error.
        notifications.error(
          $_('savedLists.add.partialFailure', {
            values: { added: error.added, requested: error.requested }
          })
        );
        await loadSavedLists();
      } else {
        notifications.error($_('savedLists.add.failed'));
      }
    } finally {
      busy = '';
    }
  }

  async function makeAndAdd(name: string, scope: ListScope) {
    const created = await makeSavedList(name, scope);
    making = false;
    await addTo(created.id, created.name);
  }
</script>

<svelte:window
  on:click={onWindowClick}
  on:keydown={onKeydown}
  on:resize={onScrollOrResize}
  on:scroll|capture={onScrollOrResize}
/>

<div class="relative" bind:this={container}>
  <button
    bind:this={trigger}
    type="button"
    onclick={toggle}
    disabled={count === 0}
    aria-expanded={open}
    aria-haspopup="menu"
    title={label || $_('savedLists.add.title')}
    class="inline-flex shrink-0 items-center gap-1.5 rounded-control font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 {variant ===
    'bar'
      ? 'chrome-control h-9 w-9 justify-center border'
      : 'px-2 py-1 text-xs hover:bg-gray-100'} {variant === 'bar'
      ? ''
      : justAdded
        ? 'text-accent-700'
        : 'text-gray-500 hover:text-accent-700'}"
  >
    {#if justAdded}
      <Check class={variant === 'bar' ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
    {:else}
      <BookmarkPlus class={variant === 'bar' ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
    {/if}
    {#if withLabel && variant !== 'bar'}
      <!--
        In the bar there is no label at all — the name is the tooltip, and the
        `title` above carries it. This control is why the data view's bar ran
        out of room: its label is the longest string in the row, "Alle 2 481
        zur Liste hinzufügen", and it sat in a `shrink-0` group, so it took its
        full width out of the bar at every size while the lookup and the mode
        switcher were squeezed into the rest.

        Everywhere else the label stays. On the company profile this control
        has a row of its own, and a wordless bookmark is the worse choice when
        nothing is competing for the space.
      -->
      <span>{label || $_('savedLists.add.title')}</span>
    {:else}
      <span class="sr-only">{label || $_('savedLists.add.title')}</span>
    {/if}
  </button>

  {#if open}
    <div
      role="menu"
      tabindex="-1"
      class="fixed z-[70] flex w-64 flex-col overflow-hidden rounded-surface border border-hairline bg-white shadow-overlay"
      style={placement}
    >
      <p class="shrink-0 border-b border-hairline px-3 py-2 text-xs text-gray-500">
        {$_('savedLists.add.heading', { values: { count } })}
      </p>

      {#if $savedLists.length > 0}
        <!--
          The lists take whatever height is left, rather than a fixed cap. The
          menu's own max-height is measured against the window when it opens,
          so a fixed one here would push "neue Liste" out of a menu that is
          already as tall as it may be — and that entry is the one a customer
          with no lists yet needs.
        -->
        <ul class="min-h-0 flex-1 overflow-y-auto py-1">
          {#each $savedLists as list (list.id)}
            <li>
              <button
                type="button"
                role="menuitem"
                onclick={() => requestAdd(list.id, list.name)}
                disabled={busy !== ''}
                class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-surface-canvas disabled:opacity-50"
              >
                {#if list.scope === 'organisation'}
                  <Users class="h-3.5 w-3.5 shrink-0 text-gray-400" />
                {:else}
                  <Bookmark class="h-3.5 w-3.5 shrink-0 text-gray-400" />
                {/if}
                <span class="min-w-0 flex-1 truncate">{list.name}</span>
                {#if busy === list.id}
                  <Loader2 class="h-3.5 w-3.5 shrink-0 animate-spin text-accent-600" />
                {:else}
                  <span class="shrink-0 text-xs tabular-nums text-gray-400">{list.entries}</span>
                {/if}
              </button>
            </li>
          {/each}
        </ul>
      {/if}

      <button
        type="button"
        role="menuitem"
        onclick={() => {
          open = false;
          making = true;
        }}
        class="flex w-full shrink-0 items-center gap-2 border-t border-hairline px-3 py-2 text-left text-sm font-medium text-accent-700 transition-colors hover:bg-accent-50"
      >
        <Plus class="h-3.5 w-3.5 shrink-0" />
        {$_('savedLists.add.newList')}
      </button>
    </div>
  {/if}
</div>

{#if making}
  <SavedListDialog onSave={makeAndAdd} onCancel={() => (making = false)} />
{/if}

{#if confirming}
  <ConfirmDialog
    title={$_('savedLists.add.confirmTitle')}
    message={$_('savedLists.add.confirmMessage', {
      values: { count: confirming.count, name: confirming.name }
    })}
    note={$_('savedLists.add.confirmNote')}
    confirmLabel={$_('savedLists.add.confirmAction')}
    onConfirm={() => {
      const wanted = confirming;
      confirming = null;
      if (wanted) void addTo(wanted.id, wanted.name);
    }}
    onCancel={() => (confirming = null)}
  />
{/if}
